import type { TFunction } from 'i18next';
import type { CognitiveFitness, SimpleMetric } from '../../model';
import type { AssessmentResultItemDataFragment } from '@/gql/graphql';
import { formatDecimalValue } from '@/utils/common';

const parseGoNoGo = (t: TFunction, items: AssessmentResultItemDataFragment[]): CognitiveFitness | null => {
    const item = items.find(i => i.result.__typename === 'GoNoGoResult');
    if (!item || item.result.__typename !== 'GoNoGoResult') {
        return null;
    }
    const mi = item.moduleInstance;
    const r = item.result;
    if (!mi?.name) {
        return null;
    }

    const metrics: SimpleMetric[] = [];

    // Composite Score
    metrics.push({
        label: t('traineeReports:performance.cognitiveFitness.score'),
        value: t('traineeReports:performance.cognitiveFitness.valueWith100', { value: Math.round(r.gngScore ?? 0) }),
        badgeLabel: r.levelLabel ?? undefined,
        badgeColor: r.levelBadgeColor ?? undefined,
    });

    // inhibitory control
    metrics.push({
        label: t('traineeReports:performance.cognitiveFitness.inhibitoryControl'),
        value: t('traineeReports:performance.cognitiveFitness.valueWith100', {
            value: formatDecimalValue(r.inhibitoryControlScore ?? 0),
        }),
    });

    // discriminability
    metrics.push({
        label: t('traineeReports:performance.cognitiveFitness.discriminability'),
        value: t('traineeReports:performance.cognitiveFitness.valueWith100', {
            value: formatDecimalValue(r.discriminabilityScore ?? 0),
        }),
    });

    // sustained attention
    metrics.push({
        label: t('traineeReports:performance.cognitiveFitness.sustainedAttention'),
        value: t('traineeReports:performance.cognitiveFitness.valueWith100', {
            value: formatDecimalValue(r.sustainedAttentionScore ?? 0),
        }),
    });

    // pressure resilience
    metrics.push({
        label: t('traineeReports:performance.cognitiveFitness.pressureResilience'),
        value: t('traineeReports:performance.cognitiveFitness.valueWith100', {
            value: formatDecimalValue(r.pressureResilienceScore ?? 0),
        }),
    });

    return {
        name: mi.name ?? '',
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier ?? '',
        description: mi.description ?? '',
        note: {
            label: r.levelLabel,
            description: r.levelDescription,
        },
        result: metrics,
    };
};

export default parseGoNoGo;
